import { Key, useEffect, useMemo, useState } from 'react';
import { DownOutlined, FolderOpenOutlined, FolderOutlined } from '@ant-design/icons';
import { DirectoryTreeProps } from 'antd/es/tree';
import { Empty, Spin, Tree, TreeDataNode } from 'antd';

import { useFetchDocumentsPage } from '../../../hooks/useFetchDocumentsPage';
import { useDocumentsNavigateTo } from '../../../hooks/useDocumentsNavigateTo';
import { useAppDispatch, useAppSelector } from 'utilities/hooks';
import { ModeDocumentsPageType } from '../../../types';
import {
    fetchDocumentTree,
    fetchDocumentTreeChildren,
    fetchDocumentTreePath,
    fetchDocuments,
    refreshDocumentTreeLevels,
} from '../../../slices/documentsActions';
import { DirectoryTreeDraggableNode } from './DirectoryTreeDraggableNode';
import { collectLoadedKeys } from 'utilities/documentTree';
import { Category } from 'interfaces/documents';

export interface TreeNodeWithMetadata extends TreeDataNode {
    metadata?: Category | null;
}

const { DirectoryTree } = Tree;

const findPathToNode = (
    nodes: TreeNodeWithMetadata[] | undefined,
    targetId: string | number,
    currentPath: Key[] = [],
): Key[] | null => {
    if (!nodes) return null;

    for (const node of nodes) {
        const newPath = [...currentPath, node.key];
        if (String(node.key) === String(targetId)) return newPath;

        if (node.children) {
            const pathInChildren = findPathToNode(node.children as TreeNodeWithMetadata[], targetId, newPath);
            if (pathInChildren) return pathInChildren;
        }
    }
    return null;
};

export const DirectoryTreeWrapper = ({ modeDocument = 'documents' }: { modeDocument?: ModeDocumentsPageType }) => {
    const dispatch = useAppDispatch();
    const { categoryId } = useFetchDocumentsPage(modeDocument);
    const { navigateTo } = useDocumentsNavigateTo(modeDocument);
    const { documentTree, loadingDocumentTree } = useAppSelector((state) => state.documents);

    const [expandedKeys, setExpandedKeys] = useState<Key[]>([0]);
    const [selectedKeys, setSelectedKeys] = useState<Key[]>([]);

    // documentsReset() empties the tree when the side menu navigates, and this
    // component is not remounted to fetch it again (/documents and
    // /documents/:categoryId render the same element).
    useEffect(() => {
        if (documentTree.length) return;

        dispatch(fetchDocumentTree());
    }, [dispatch, documentTree.length]);

    // rc-tree will not lazily re-fetch a key it has already loaded, so drive its
    // loaded set off the tree: a branch left expanded but empty by a refresh
    // (a folder moved in from elsewhere, say) fetches itself again.
    const loadedKeys = useMemo(() => collectLoadedKeys(documentTree), [documentTree]);

    // Load every level along the ancestor chain in one request, then expand to
    // the active folder (the slice merges the levels into the tree).
    useEffect(() => {
        if (!categoryId) return;

        dispatch(fetchDocumentTreePath({ nodeId: Number(categoryId) })).then((action: any) => {
            const path = action?.payload?.data?.path;
            if (path) {
                setExpandedKeys(path);
                setSelectedKeys([Number(categoryId)]);
            }
        });
    }, [dispatch, categoryId]);

    const onLoadData = async (node: { key: Key; children?: unknown[] }) => {
        if (node.children && node.children.length) return;
        await dispatch(fetchDocumentTreeChildren({ parentId: Number(node.key) }));
    };

    const onSelect: DirectoryTreeProps['onSelect'] = (selectedKeys, info) => {
        const categoryId = Number(selectedKeys[0]);

        if (findPathToNode(documentTree, categoryId)) {
            navigateTo(categoryId);
            setSelectedKeys([categoryId]);
        }
    };

    const onCategoryMoved = (affectedFolderIds: Array<number | null | undefined>) => {
        dispatch(refreshDocumentTreeLevels(affectedFolderIds));
        dispatch(fetchDocuments(categoryId));
    };

    const parentMap: { [key: number]: number } = {};
    const populateParentMap = (nodes: TreeNodeWithMetadata[]) => {
        nodes.forEach((node) => {
            if (node.metadata?.parent_id) {
                parentMap[Number(node.key)] = node.metadata.parent_id;
            }
            if (node.children) {
                populateParentMap(node.children);
            }
        });
    };
    populateParentMap(documentTree);

    return (
        <Spin spinning={loadingDocumentTree} tip={'Loading...'}>
            {documentTree.length ? (
                <DirectoryTree
                    showLine
                    // Every tree node is a folder; isLeaf nodes would otherwise render as files.
                    icon={(node: { expanded?: boolean }) =>
                        node.expanded ? <FolderOpenOutlined /> : <FolderOutlined />
                    }
                    titleRender={(nodeData) => {
                        return (
                            <DirectoryTreeDraggableNode
                                nodeData={nodeData as TreeNodeWithMetadata}
                                onMove={onCategoryMoved}
                            />
                        );
                    }}
                    treeData={documentTree}
                    loadData={onLoadData}
                    loadedKeys={loadedKeys}
                    expandedKeys={expandedKeys}
                    selectedKeys={selectedKeys}
                    switcherIcon={<DownOutlined />}
                    style={{ whiteSpace: 'nowrap', overflowX: 'auto', overflowY: 'hidden' }}
                    onSelect={onSelect}
                    onExpand={setExpandedKeys}
                />
            ) : (
                <Empty />
            )}
        </Spin>
    );
};
